/**
 * Innehåll för de lokala områdessidorna (Marbella, Estepona, Cancelada).
 *
 * Sidorna är avsiktligt substantiella – varje sida har eget innehåll om
 * restider, läge, årskurser och varför familjer just därifrån väljer skolan.
 * Restider anges alltid som ungefärliga intervall, aldrig som löften.
 */

export type AreaKey = "marbella" | "estepona" | "cancelada";

export type TravelRow = {
  from: string;
  distance: string;
  calm: string;
  rush: string;
};

export type AreaSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

export type AreaFaq = { question: string; answer: string };

export type AreaContent = {
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  title: string;
  intro: string;
  travelHeading: string;
  travelNote: string;
  travelLabels: { from: string; distance: string; calm: string; rush: string };
  travel: TravelRow[];
  sectionsBeforeMap: AreaSection[];
  sectionsAfterMap: AreaSection[];
  faqHeading: string;
  faq: AreaFaq[];
};

type Locale = "sv" | "en";

const GRADES_SV = {
  heading: "Vilka åldrar och årskurser tar vi emot?",
  paragraphs: [
    "Skolan tar emot barn i förskolan från 3 år (2 år i undantagsfall) och elever till och med årskurs 6. För högstadiet och gymnasiet erbjuder vi studieplats och handledning på skolan medan eleven läser via Sofia Distans eller Hermods.",
    "Undervisningen följer den svenska läroplanen och sker på svenska, med spanska och engelska varje vecka. Skolan är godkänd av Skolverket för kompletterande svensk undervisning och står under tillsyn av Junta de Andalucía, den spanska utbildningsmyndighet som ger tillstånd till och inspekterar alla skolor i regionen. Vi är en fristående skola med egen finansiering, vilket innebär att avgiften är densamma för alla familjer — inga villkorade nivåer och ingen ansökan som först måste beviljas.",
  ],
  bullets: [
    "Förskola från 3 år (2 år i undantagsfall)",
    "Förskoleklass och årskurs 1–6",
    "Distansstudier för högstadiet och gymnasiet på plats i skolan",
    "Mål: högst 16 elever per grupp; storleken kan variera",
  ],
};

const GRADES_EN = {
  heading: "Which ages and year groups do we take?",
  paragraphs: [
    "We take preschool children from the age of 3 (from 2 in exceptional cases) and pupils up to and including year 6. For secondary school and sixth form we offer a study place and supervision at the school while the pupil studies through Sofia Distans or Hermods.",
    "Teaching follows the Swedish national curriculum and is delivered in Swedish, with Spanish and English every week. The school is approved by the Swedish National Agency for Education for supplementary Swedish tuition and is supervised by the Junta de Andalucía, the Spanish education authority that authorises and inspects every school in the region. We are an independent school with our own funding, which means the fee is the same for all families — no conditional tiers and no application that must be approved first.",
  ],
  bullets: [
    "Preschool from age 3 (from 2 in exceptional cases)",
    "Preschool class and years 1–6",
    "Distance learning for secondary and sixth form on site",
    "Target: no more than 16 pupils per group; sizes may vary",
  ],
};

const LABELS_SV = { from: "Från", distance: "Avstånd", calm: "Lugn trafik", rush: "Rusningstid" };
const LABELS_EN = { from: "From", distance: "Distance", calm: "Off-peak", rush: "Rush hour" };

const TRAVEL_NOTE_SV =
  "Tiderna nedan är ungefärliga och bygger på vad familjer hos oss brukar uppleva. Trafiken varierar med säsong, väder och skoltider – provkör gärna sträckan en vardagsmorgon innan ni bestämmer var ni ska bo.";
const TRAVEL_NOTE_EN =
  "The times below are approximate and based on what families at the school typically experience. Traffic varies with the season, the weather and school hours – it is worth driving the route on a weekday morning before you decide where to live.";

export const AREAS: Record<AreaKey, Record<Locale, AreaContent>> = {
  marbella: {
    sv: {
      metaTitle: "Svensk skola nära Marbella – avstånd, restider och årskurser",
      metaDescription:
        "Svensk skola nära Marbella: avstånd och restider från Marbella, San Pedro och Nueva Andalucía, skolans läge i Cancelada, årskurser och hur ni bokar besök.",
      eyebrow: "Område",
      title: "Svensk skola nära Marbella",
      intro:
        "Bor ni i Marbella, San Pedro eller Nueva Andalucía ligger skolan västerut längs kusten, i Cancelada mellan Marbella och Estepona. Här går vi igenom avstånd, restider, vilka årskurser vi tar emot och varför familjer från Marbella-området väljer oss.",
      travelHeading: "Avstånd och restider från Marbella-området",
      travelNote: TRAVEL_NOTE_SV,
      travelLabels: LABELS_SV,
      travel: [
        { from: "Marbella centrum", distance: "ca 20 km", calm: "20–25 min", rush: "30–40 min" },
        { from: "Puerto Banús", distance: "ca 14 km", calm: "15–20 min", rush: "25–30 min" },
        { from: "Nueva Andalucía", distance: "ca 15 km", calm: "15–20 min", rush: "25–35 min" },
        { from: "San Pedro de Alcántara", distance: "ca 11 km", calm: "12–18 min", rush: "20–30 min" },
        { from: "Guadalmina / Golden Mile-sidan", distance: "ca 9 km", calm: "10–15 min", rush: "18–25 min" },
      ],
      sectionsBeforeMap: [
        {
          heading: "Skolans läge – varför västerut kan vara enklare",
          paragraphs: [
            "Skolan ligger i Cancelada, ett bostadsområde mellan Estepona och Marbella, precis intill A-7 och nära avfarterna mot kusten. För familjer i Marbella innebär det att skolvägen går motsatt riktning mot den tyngsta morgontrafiken, som rör sig österut in mot Marbella och Málaga.",
            "Det är den detalj som brukar överraska mest: en skola som ligger längre bort på kartan kan ta kortare tid att nå än en skola inne i stan, helt enkelt för att ni kör åt andra hållet än alla andra.",
          ],
        },
        {
          heading: "Varför familjer från Marbella väljer skolan",
          paragraphs: [
            "De flesta familjer som söker sig hit från Marbella-området gör det av tre skäl: barnen ska kunna gå tillbaka till svensk skola utan att tappa tempo, grupperna ska vara små nog att alla vuxna känner alla barn, och skoldagen ska gå att kombinera med idrott.",
          ],
          bullets: [
            "Svensk läroplan och undervisning på svenska – enklare återgång till Sverige",
            "Vi strävar efter högst 16 elever per grupp; storleken kan variera",
            "Fotbollsakademi tillsammans med Malmö FF och en padel- och tennisprofil",
            "Skolskjuts finns inte, men många familjer i samma område samåker",
          ],
        },
      ],
      sectionsAfterMap: [
        GRADES_SV,
        {
          heading: "Vardagen om ni pendlar från Marbella",
          paragraphs: [
            "Skoldagen har fasta tider, och fritidsverksamheten gör att hämtningen kan läggas senare på eftermiddagen – vilket för många pendlande familjer är skillnaden mellan att det fungerar och inte.",
            "På loven arrangerar vi camper, bland annat fotbollscamp i juni tillsammans med Malmö FF, vilket gör att lovveckorna inte alltid behöver lösas hemma.",
          ],
        },
        {
          heading: "Besök skolan innan ni bestämmer",
          paragraphs: [
            "Vi rekommenderar alltid att komma hit under en vanlig skoldag. Ni får gå runt i lokalerna, träffa personalen och se hur grupperna faktiskt fungerar. Ta gärna med barnen. Ansökningar tas emot löpande och det går bra att börja mitt i terminen om det finns plats.",
          ],
        },
      ],
      faqHeading: "Vanliga frågor från familjer i Marbella",
      faq: [
        {
          question: "Hur lång tid tar det till skolan från Marbella centrum?",
          answer:
            "Räkna med ungefär 20–25 minuter i lugn trafik och 30–40 minuter under morgonrusningen. Sträckan är cirka 20 kilometer via A-7.",
        },
        {
          question: "Finns det skolbuss från Marbella?",
          answer:
            "Vi driver ingen egen skolskjuts. Många familjer i samma område löser det genom samåkning, och skolan hjälper gärna till att sätta familjer i kontakt med varandra.",
        },
        {
          question: "Tar ni emot elever mitt under läsåret?",
          answer:
            "Ja, ansökningar tas emot löpande och elever kan börja mitt i terminen när det finns plats i gruppen.",
        },
        {
          question: "Kan barnen läsa vidare efter årskurs 6?",
          answer:
            "Ja. Högstadie- och gymnasieelever har studieplats och handledning på skolan medan de läser via Sofia Distans eller Hermods.",
        },
      ],
    },
    en: {
      metaTitle: "Swedish school near Marbella – travel times and years",
      metaDescription:
        "A Swedish school near Marbella: travel times from Marbella, San Pedro and Nueva Andalucía, our location in Cancelada, year groups and how to book a visit.",
      eyebrow: "Area",
      title: "Swedish school near Marbella",
      intro:
        "If you live in Marbella, San Pedro or Nueva Andalucía, the school lies west along the coast in Cancelada, between Marbella and Estepona. Here are the distances, travel times, the year groups we take and why families from the Marbella area choose us.",
      travelHeading: "Distances and travel times from the Marbella area",
      travelNote: TRAVEL_NOTE_EN,
      travelLabels: LABELS_EN,
      travel: [
        { from: "Marbella centre", distance: "approx. 20 km", calm: "20–25 min", rush: "30–40 min" },
        { from: "Puerto Banús", distance: "approx. 14 km", calm: "15–20 min", rush: "25–30 min" },
        { from: "Nueva Andalucía", distance: "approx. 15 km", calm: "15–20 min", rush: "25–35 min" },
        { from: "San Pedro de Alcántara", distance: "approx. 11 km", calm: "12–18 min", rush: "20–30 min" },
        { from: "Guadalmina / Golden Mile side", distance: "approx. 9 km", calm: "10–15 min", rush: "18–25 min" },
      ],
      sectionsBeforeMap: [
        {
          heading: "Our location – why heading west can be easier",
          paragraphs: [
            "The school sits in Cancelada, a residential area between Estepona and Marbella, right next to the A-7 and close to the coastal exits. For families in Marbella that means the school run goes against the heaviest morning traffic, which moves east towards Marbella and Málaga.",
            "That is the detail that surprises most people: a school that looks further away on the map can take less time to reach than one in town, simply because you are driving the other way.",
          ],
        },
        {
          heading: "Why families from Marbella choose the school",
          paragraphs: [
            "Most families who come to us from the Marbella area do so for three reasons: their children should be able to return to a Swedish class without losing pace, the groups should be small enough that every adult knows every child, and the school day should work alongside sport.",
          ],
          bullets: [
            "Swedish curriculum, taught in Swedish – an easier return to Sweden",
            "We aim for no more than 16 pupils per group; sizes may vary",
            "A football academy with Malmö FF and a padel and tennis profile",
            "There is no school bus, but many families in the same area share lifts",
          ],
        },
      ],
      sectionsAfterMap: [
        GRADES_EN,
        {
          heading: "Everyday life if you commute from Marbella",
          paragraphs: [
            "The school day has fixed hours, and after-school care means pick-up can be later in the afternoon – which for commuting families is often the difference between workable and not. Lunch is cooked at the school and is currently served on regular school days at no extra charge.",
            "During the holidays we run camps, including a football camp in June together with Malmö FF, so holiday weeks do not always have to be solved at home.",
          ],
        },
        {
          heading: "Visit before you decide",
          paragraphs: [
            "We always recommend coming during an ordinary school day. You can walk through the premises, meet the staff and see how the groups actually work. Bring the children. Applications are accepted all year round and pupils can start mid-term when there is space.",
          ],
        },
      ],
      faqHeading: "Common questions from families in Marbella",
      faq: [
        {
          question: "How long does it take to reach the school from central Marbella?",
          answer:
            "Expect roughly 20–25 minutes off-peak and 30–40 minutes during the morning rush. The distance is about 20 kilometres via the A-7.",
        },
        {
          question: "Is there a school bus from Marbella?",
          answer:
            "We do not run our own school transport. Many families in the same area share lifts, and the school is happy to put families in touch with each other.",
        },
        {
          question: "Do you take pupils mid-year?",
          answer:
            "Yes. Applications are accepted all year round and pupils can start mid-term when there is space in the group.",
        },
        {
          question: "Can children continue after year 6?",
          answer:
            "Yes. Secondary and sixth-form pupils have a study place and supervision at the school while studying through Sofia Distans or Hermods.",
        },
      ],
    },
  },

  estepona: {
    sv: {
      metaTitle: "Skola i Estepona – svensk skola i Cancelada",
      metaDescription:
        "Skola i Estepona för svenska familjer: restider från Estepona centrum, Costalita, El Paraíso och New Golden Mile, skolans läge, årskurser och besök.",
      eyebrow: "Område",
      title: "Skola i Estepona",
      intro:
        "Skolan ligger i Cancelada, som hör till Estepona kommun, strax öster om staden. För familjer i Estepona centrum, Costalita, El Paraíso och New Golden Mile är skolvägen kort. Här är avstånd, restider, årskurser och vad som gäller praktiskt.",
      travelHeading: "Avstånd och restider inom Estepona",
      travelNote: TRAVEL_NOTE_SV,
      travelLabels: LABELS_SV,
      travel: [
        { from: "Estepona centrum", distance: "ca 10 km", calm: "12–15 min", rush: "15–25 min" },
        { from: "Costalita", distance: "under 1 km", calm: "2–5 min", rush: "2–5 min" },
        { from: "El Paraíso", distance: "ca 4 km", calm: "6–9 min", rush: "10–15 min" },
        { from: "New Golden Mile", distance: "ca 3–6 km", calm: "5–10 min", rush: "10–18 min" },
        { from: "Benahavís", distance: "ca 12 km", calm: "15–20 min", rush: "20–30 min" },
      ],
      sectionsBeforeMap: [
        {
          heading: "Skolan hör hemma i Estepona",
          paragraphs: [
            "Cancelada är en av Esteponas östliga kärnor, med egen vardagsservice, matbutiker, restauranger och padelbanor inom några minuter. Skolan ligger i Urb Costalita, nära både kusten och A-7, vilket gör den lätt att nå från hela kommunen.",
            "Att skolan ligger i Estepona snarare än inne i Marbella märks framför allt på tempot: mindre trafik runt lämning och hämtning, och kortare väg till stranden och idrottsanläggningarna som används under skoldagen.",
          ],
        },
        {
          heading: "Varför familjer i Estepona väljer skolan",
          paragraphs: [
            "För familjer i Estepona är närheten den självklara fördelen, men de flesta nämner något annat som avgörande: att skolan är liten. Personalen känner varje barn, och nya elever kommer in i gemenskapen snabbt även mitt under läsåret.",
          ],
          bullets: [
            "Kort skolväg – för många i närområdet under tio minuter",
            "Svensk läroplan, undervisning på svenska, spanska och engelska varje vecka",
            "Vi strävar efter högst 16 elever per grupp; storleken kan variera",
            "Fritids efter skoldagen",
          ],
        },
      ],
      sectionsAfterMap: [
        GRADES_SV,
        {
          heading: "Skolval i Estepona – hur vi skiljer oss",
          paragraphs: [
            "I Estepona finns spanska kommunala och privata skolor samt internationella skolor med undervisning på engelska. Valet handlar sällan om vilken skola som är bäst i allmänhet, utan om vilket system som passar familjens plan.",
            "Ska familjen stanna länge och rota sig lokalt talar mycket för spansk skola. Är horisonten osäker, eller vill ni hålla dörren öppen mot Sverige, väger kontinuiteten i den svenska läroplanen tyngre. Hos oss läser barnen spanska varje vecka och möter språket i vardagen ändå.",
          ],
        },
        {
          heading: "Boka besök",
          paragraphs: [
            "Bor ni redan i Estepona är det enkelt att svänga förbi. Hör av er till rektor så bokar vi ett besök under en vanlig skoldag – det säger mer än vilken broschyr som helst. Ansökningar tas emot löpande.",
          ],
        },
      ],
      faqHeading: "Vanliga frågor från familjer i Estepona",
      faq: [
        {
          question: "Var i Estepona ligger skolan?",
          answer:
            "Skolan ligger i Urb Costalita 1 i Cancelada, 29680 Estepona, strax intill A-7 och nära kusten.",
        },
        {
          question: "Hur lång tid tar det från Estepona centrum?",
          answer:
            "Ungefär 12–15 minuter i lugn trafik och upp till 25 minuter under rusningstid, en sträcka på cirka 10 kilometer.",
        },
        {
          question: "Erbjuder ni fritids?",
          answer:
            "Ja, det finns fritidsverksamhet i anslutning till skoldagen, vilket gör hämtningen mer flexibel för arbetande föräldrar.",
        },
        {
          question: "Vilka språk används i undervisningen?",
          answer:
            "Undervisningen sker på svenska enligt svensk läroplan, med spanska och engelska varje vecka.",
        },
      ],
    },
    en: {
      metaTitle: "School in Estepona – Swedish school in Cancelada",
      metaDescription:
        "A school in Estepona for Swedish families: travel times from central Estepona, Costalita and El Paraíso, our location, year groups and how to book a visit.",
      eyebrow: "Area",
      title: "School in Estepona",
      intro:
        "The school is in Cancelada, part of the municipality of Estepona, just east of the town. For families in central Estepona, Costalita, El Paraíso and the New Golden Mile the school run is short. Here are the distances, travel times, year groups and practical details.",
      travelHeading: "Distances and travel times within Estepona",
      travelNote: TRAVEL_NOTE_EN,
      travelLabels: LABELS_EN,
      travel: [
        { from: "Central Estepona", distance: "approx. 10 km", calm: "12–15 min", rush: "15–25 min" },
        { from: "Costalita", distance: "under 1 km", calm: "2–5 min", rush: "2–5 min" },
        { from: "El Paraíso", distance: "approx. 4 km", calm: "6–9 min", rush: "10–15 min" },
        { from: "New Golden Mile", distance: "approx. 3–6 km", calm: "5–10 min", rush: "10–18 min" },
        { from: "Benahavís", distance: "approx. 12 km", calm: "15–20 min", rush: "20–30 min" },
      ],
      sectionsBeforeMap: [
        {
          heading: "The school belongs to Estepona",
          paragraphs: [
            "Cancelada is one of Estepona's eastern hubs, with its own everyday services, supermarkets, restaurants and padel courts within minutes. The school is in Urb Costalita, close to both the coast and the A-7, which makes it easy to reach from across the municipality.",
            "Being in Estepona rather than central Marbella shows mainly in the pace: less traffic at drop-off and pick-up, and a shorter distance to the beach and the sports facilities used during the school day.",
          ],
        },
        {
          heading: "Why families in Estepona choose the school",
          paragraphs: [
            "For families in Estepona proximity is the obvious advantage, but most name something else as decisive: the school is small. Staff know every child, and new pupils settle into the group quickly even mid-year.",
          ],
          bullets: [
            "A short school run – under ten minutes for many in the area",
            "Swedish curriculum, taught in Swedish, with Spanish and English every week",
            "We aim for no more than 16 pupils per group; sizes may vary",
            "After-school care after the school day",
          ],
        },
      ],
      sectionsAfterMap: [
        GRADES_EN,
        {
          heading: "Choosing a school in Estepona – how we differ",
          paragraphs: [
            "Estepona has Spanish state and private schools as well as international schools taught in English. The choice is rarely about which school is best in general, but about which system suits your family's plan.",
            "If you intend to stay long term and put down local roots, a Spanish school makes a lot of sense. If the horizon is uncertain, or you want to keep the door to Sweden open, continuity in the Swedish curriculum weighs more. With us children study Spanish every week and meet the language in daily life regardless.",
          ],
        },
        {
          heading: "Book a visit",
          paragraphs: [
            "If you already live in Estepona it is easy to drop by. Contact the head teacher and we will arrange a visit during an ordinary school day – it tells you more than any brochure. Applications are accepted all year round.",
          ],
        },
      ],
      faqHeading: "Common questions from families in Estepona",
      faq: [
        {
          question: "Where in Estepona is the school?",
          answer:
            "The school is at Urb Costalita 1 in Cancelada, 29680 Estepona, right next to the A-7 and close to the coast.",
        },
        {
          question: "How long does it take from central Estepona?",
          answer:
            "Roughly 12–15 minutes off-peak and up to 25 minutes in rush hour, a distance of about 10 kilometres.",
        },
        {
          question: "Do you offer after-school care?",
          answer:
            "Yes, after-school care runs alongside the school day, which makes pick-up more flexible for working parents.",
        },
        {
          question: "Which languages are used in teaching?",
          answer:
            "Teaching is in Swedish following the Swedish curriculum, with Spanish and English every week.",
        },
      ],
    },
  },

  cancelada: {
    sv: {
      metaTitle: "Skola i Cancelada – svensk skola i Urb Costalita, Estepona",
      metaDescription:
        "Skola i Cancelada: skolans läge i Urb Costalita, gångavstånd från närområdet, vardagslogistik, årskurser och hur ni bokar besök eller anmäler er.",
      eyebrow: "Område",
      title: "Skola i Cancelada",
      intro:
        "Skolan ligger mitt i Cancelada, i Urb Costalita strax söder om A-7. För familjer i närområdet betyder det korta avstånd, ofta gång- eller cykelavstånd, och en vardag där skolan, matbutiken och idrotten ligger inom några minuter.",
      travelHeading: "Avstånd i närområdet",
      travelNote: TRAVEL_NOTE_SV,
      travelLabels: LABELS_SV,
      travel: [
        { from: "Costalita", distance: "under 1 km", calm: "2–5 min", rush: "2–5 min" },
        { from: "Cancelada by", distance: "ca 1–2 km", calm: "3–6 min", rush: "5–10 min" },
        { from: "Selwo-området", distance: "ca 5 km", calm: "7–10 min", rush: "10–15 min" },
        { from: "El Paraíso / Atalaya", distance: "ca 4 km", calm: "6–9 min", rush: "10–15 min" },
        { from: "Estepona centrum", distance: "ca 10 km", calm: "12–15 min", rush: "15–25 min" },
      ],
      sectionsBeforeMap: [
        {
          heading: "Ett närområde som fungerar för barnfamiljer",
          paragraphs: [
            "Cancelada har kvar mycket av sin bykänsla samtidigt som området vuxit med nya bostadsområden. Här finns matbutiker, apotek, restauranger, padelbanor och strand inom några minuter – och en stor del av det ligger inom räckhåll utan att sätta sig i bilen.",
            "För familjer som bor i Costalita eller intilliggande urbanisationer är skolan så nära att många barn går eller cyklar till skolan tillsammans med kompisar. Det är ovanligt längs den här delen av kusten, där det mesta annars görs med bil.",
          ],
        },
        {
          heading: "Vardagslogistik",
          paragraphs: [
            "Korta avstånd gör en påtaglig skillnad över en vecka: lämning tar minuter, hämtning kan ske i anslutning till fritids, och det går att komma tillbaka till skolan på eftermiddagen för träning utan att lägga en timme i bilen.",
          ],
          bullets: [
            "Lunch lagas på skolan och serveras utan extra kostnad för närvarande",
            "Fritids efter skoldagen",
            "Fotbollsakademi med Malmö FF samt padel- och tennisprofil",
            "Camper på loven, bland annat fotbollscamp i juni",
          ],
        },
      ],
      sectionsAfterMap: [
        GRADES_SV,
        {
          heading: "Att flytta till Cancelada",
          paragraphs: [
            "Många familjer väljer bostad först och skola sedan – här går det ofta att göra tvärtom. Om skolan är avgörande för vardagen kan det vara värt att titta på bostad i Cancelada, Costalita eller El Paraíso innan ni tittar längre österut.",
            "Vi svarar gärna på frågor om området, även sådant som inte handlar om skolan. Vi bor och arbetar här själva.",
          ],
        },
        {
          heading: "Kom och hälsa på",
          paragraphs: [
            "Bor ni redan i Cancelada är ni välkomna att komma förbi under en vanlig skoldag. Hör av er så bokar vi en tid. Ansökningar tas emot löpande och det går bra att börja mitt i terminen när det finns plats.",
          ],
        },
      ],
      faqHeading: "Vanliga frågor om skolan i Cancelada",
      faq: [
        {
          question: "Vilken adress har skolan?",
          answer: "Urb Costalita 1, Cancelada, 29680 Estepona.",
        },
        {
          question: "Kan barnen gå eller cykla till skolan?",
          answer:
            "Många barn i Costalita och närliggande urbanisationer gör det. Avstånden är korta, men vägvalet beror på var ni bor – kom gärna och titta på plats.",
        },
        {
          question: "Serveras lunch på skolan?",
          answer:
            "Ja, lunch lagas på skolan och serveras under ordinarie skoldagar utan extra kostnad för närvarande. Matsedeln publiceras löpande på webbplatsen.",
        },
        {
          question: "Vilka årskurser finns i Cancelada?",
          answer:
            "Förskola från 3 år (2 år i undantagsfall), förskoleklass och årskurs 1–6, samt studieplats för distansstudier på högstadiet och gymnasiet.",
        },
      ],
    },
    en: {
      metaTitle: "School in Cancelada – Swedish school in Estepona",
      metaDescription:
        "A school in Cancelada: our location in Urb Costalita, walking distance from the neighbourhood, everyday logistics, year groups and how to visit or apply.",
      eyebrow: "Area",
      title: "School in Cancelada",
      intro:
        "The school sits in the middle of Cancelada, in Urb Costalita just south of the A-7. For families nearby that means short distances, often walking or cycling distance, and a daily routine where school, the supermarket and sport are minutes apart.",
      travelHeading: "Distances in the neighbourhood",
      travelNote: TRAVEL_NOTE_EN,
      travelLabels: LABELS_EN,
      travel: [
        { from: "Costalita", distance: "under 1 km", calm: "2–5 min", rush: "2–5 min" },
        { from: "Cancelada village", distance: "approx. 1–2 km", calm: "3–6 min", rush: "5–10 min" },
        { from: "Selwo area", distance: "approx. 5 km", calm: "7–10 min", rush: "10–15 min" },
        { from: "El Paraíso / Atalaya", distance: "approx. 4 km", calm: "6–9 min", rush: "10–15 min" },
        { from: "Central Estepona", distance: "approx. 10 km", calm: "12–15 min", rush: "15–25 min" },
      ],
      sectionsBeforeMap: [
        {
          heading: "A neighbourhood that works for families",
          paragraphs: [
            "Cancelada keeps much of its village feel while growing with new residential areas. There are supermarkets, pharmacies, restaurants, padel courts and the beach within minutes – much of it within reach without getting in the car.",
            "For families living in Costalita or the neighbouring urbanisations the school is close enough that many children walk or cycle there with friends. That is unusual along this part of the coast, where most things are done by car.",
          ],
        },
        {
          heading: "Everyday logistics",
          paragraphs: [
            "Short distances make a real difference across a week: drop-off takes minutes, pick-up can be tied to after-school care, and it is possible to come back for training in the afternoon without spending an hour in the car.",
          ],
          bullets: [
            "Lunch is cooked at the school and currently served at no extra charge",
            "After-school care after the school day",
            "Football academy with Malmö FF plus a padel and tennis profile",
            "Holiday camps, including a football camp in June",
          ],
        },
      ],
      sectionsAfterMap: [
        GRADES_EN,
        {
          heading: "Moving to Cancelada",
          paragraphs: [
            "Many families choose a home first and a school second – here you can often do the opposite. If the school is decisive for your daily routine, it is worth looking at homes in Cancelada, Costalita or El Paraíso before looking further east.",
            "We are happy to answer questions about the area, including things that have nothing to do with school. We live and work here ourselves.",
          ],
        },
        {
          heading: "Come and say hello",
          paragraphs: [
            "If you already live in Cancelada you are welcome to drop by during an ordinary school day. Get in touch and we will arrange a time. Applications are accepted all year round and pupils can start mid-term when there is space.",
          ],
        },
      ],
      faqHeading: "Common questions about the school in Cancelada",
      faq: [
        {
          question: "What is the school's address?",
          answer: "Urb Costalita 1, Cancelada, 29680 Estepona.",
        },
        {
          question: "Can children walk or cycle to school?",
          answer:
            "Many children in Costalita and nearby urbanisations do. Distances are short, but the route depends on where you live – come and take a look on site.",
        },
        {
          question: "Is lunch served at the school?",
          answer:
            "Yes, lunch is cooked at the school and currently served on regular school days at no extra charge. The menu is published on the website.",
        },
        {
          question: "Which year groups are in Cancelada?",
          answer:
            "Preschool from age 3 (from 2 in exceptional cases), preschool class and years 1–6, plus a study place for distance learning at secondary and sixth-form level.",
        },
      ],
    },
  },
};

/** JSON-LD för en områdessida: skolan med adress och geo, plus FAQ. */
export function areaJsonLd(content: AreaContent, url: string, locale: Locale) {
  return [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "School",
        name: "Swedish International School",
        url,
        inLanguage: locale,
        address: {
          "@type": "PostalAddress",
          streetAddress: "Urb. Costalita 1, Cancelada",
          addressLocality: "Estepona",
          postalCode: "29680",
          addressRegion: "Málaga",
          addressCountry: "ES",
        },
        geo: { "@type": "GeoCoordinates", latitude: 36.45995, longitude: -5.04781 },
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: content.faq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      }),
    },
  ];
}
